/**
 * Cost per unit by supplier.
 *
 * A tiny inline SVG rather than a charting library: one line per supplier over the batches
 * received from them, plotted on landed unit cost. It answers one question, which is whether
 * a supplier is drifting up, and a 90 kilobyte dependency is not worth that.
 *
 * Colours come from the token palette helper so they survive a rebrand and stay distinct in
 * the dark console.
 */
import { useMemo } from 'react';
import { formatMoney } from '@/lib/format';
import { seriesColor } from './constants';
import type { HistoryResponse } from './types';

export interface CostBySupplierChartProps {
  receipts: HistoryResponse['receipts'];
}

interface Series {
  supplier: string;
  points: { x: number; y: number; date: string; cents: number }[];
  color: string;
}

const WIDTH = 320;
const HEIGHT = 110;
const PAD_LEFT = 34;
const PAD_BOTTOM = 16;
const PAD_TOP = 8;

export function CostBySupplierChart({ receipts }: CostBySupplierChartProps) {
  const { series, min, max } = useMemo(() => {
    const dated = receipts
      .filter((r) => r.received_date !== null && r.landed_unit_cents > 0)
      .sort((a, b) => (a.received_date ?? '').localeCompare(b.received_date ?? ''));

    if (dated.length === 0) return { series: [] as Series[], min: 0, max: 0 };

    const times = dated.map((r) => Date.parse(`${r.received_date ?? ''}T00:00:00Z`));
    const tMin = Math.min(...times);
    const tMax = Math.max(...times);
    const costs = dated.map((r) => r.landed_unit_cents);
    const cMin = Math.min(...costs);
    const cMax = Math.max(...costs);
    const cSpan = cMax - cMin || 1;
    const tSpan = tMax - tMin || 1;

    const bySupplier = new Map<string, Series>();
    dated.forEach((r) => {
      const name = r.supplier_name ?? 'Unknown supplier';
      const entry =
        bySupplier.get(name) ??
        ({ supplier: name, points: [], color: seriesColor(bySupplier.size) } as Series);
      const t = Date.parse(`${r.received_date ?? ''}T00:00:00Z`);
      entry.points.push({
        x: PAD_LEFT + ((t - tMin) / tSpan) * (WIDTH - PAD_LEFT - 4),
        y:
          PAD_TOP +
          (1 - (r.landed_unit_cents - cMin) / cSpan) * (HEIGHT - PAD_TOP - PAD_BOTTOM),
        date: r.received_date ?? '',
        cents: r.landed_unit_cents,
      });
      bySupplier.set(name, entry);
    });

    return { series: [...bySupplier.values()], min: cMin, max: cMax };
  }, [receipts]);

  if (series.length === 0) {
    return <p className="o-muted">No received batches with a cost yet.</p>;
  }

  return (
    <figure className="stock-chart">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={`Landed unit cost by supplier, ${series.length} suppliers, between ${formatMoney(min, { showCents: true })} and ${formatMoney(max, { showCents: true })}`}
        preserveAspectRatio="none"
        className="stock-chart__svg"
      >
        <line
          x1={PAD_LEFT}
          y1={PAD_TOP}
          x2={PAD_LEFT}
          y2={HEIGHT - PAD_BOTTOM}
          stroke="var(--border)"
        />
        <line
          x1={PAD_LEFT}
          y1={HEIGHT - PAD_BOTTOM}
          x2={WIDTH}
          y2={HEIGHT - PAD_BOTTOM}
          stroke="var(--border)"
        />
        {series.map((entry) => (
          <g key={entry.supplier}>
            {entry.points.length > 1 ? (
              <path
                d={entry.points
                  .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
                  .join(' ')}
                fill="none"
                stroke={entry.color}
                strokeWidth="1.5"
              />
            ) : null}
            {entry.points.map((p) => (
              <circle
                key={`${entry.supplier}-${p.date}-${p.cents}`}
                cx={p.x}
                cy={p.y}
                r="2.5"
                fill={entry.color}
              >
                <title>{`${entry.supplier}, ${p.date}, ${formatMoney(p.cents, { showCents: true })} per unit`}</title>
              </circle>
            ))}
          </g>
        ))}
      </svg>
      <figcaption className="stock-chart__legend">
        {series.map((entry) => {
          const last = entry.points[entry.points.length - 1];
          return (
            <span key={entry.supplier}>
              <span className="stock-chart__swatch" style={{ background: entry.color }} />
              {entry.supplier}
              {last ? ` ${formatMoney(last.cents, { showCents: true })}` : ''}
            </span>
          );
        })}
      </figcaption>
    </figure>
  );
}
